import { Injectable } from '@angular/core';
import { FieldContext } from '../utils/fieldcontext';
import { FieldType } from '../utils/fieldtype';
import { Tag } from '../utils/tag';
import { ContentControlId } from './document-monitors/content-control-id';
import { LoggingService } from './logging.service';

/**
 * Inserts bound ContentControl objects into the Word document
 */
@Injectable({ providedIn: 'root' })
export class ContentControlService {
  // PNG of "Placeholder Image"
  private static readonly PLACEHOLDER_IMAGE =
    'iVBORw0KGgoAAAANSUhEUgAAAH4AAAA6CAIAAAD5iHqlAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsMAAA7DAcdvqGQAAA' +
    'LBSURBVHhe7ZrRdcMgDEUzVwbKPJkmy2SY1DFgCQkF7BP32e27f4AQj2s3Hz69vAgIqodB9TCoHgbVw6B6GFQPg+phUD0MqodB9TA66h+3S5vbI1c8' +
    '79c8db0/89zOSKglxTq6maVg4wkjbFU/kVJR/VaG1ZeQkipNUf1WVqu3uVvXkLmCvaKrcFe0f2+qoFLfSJgxHVSDVmZVfXtIgc41Ekn26p1NVqtX58' +
    '9T7ho2YKF5S81wQXSCum5QkteHM0vH0UjXa2otlwn5+m+9hHCi0gYZ+9x1By/SFPiW7bGdMJll6PbbidxPzQSRBtiovlhrvEEKdac3cyjpGGSMjpxp' +
    't5Bz6vVWyneFyezrzQkbIg2w/rfe4NWHQSMvNR/vWasq9/y6+qhhi6BFn2+rl2ERE10jati/hYRKJwwcUVeYzL7enLAiUljh+bZ6l0E6ZFG+ZWQub3' +
    'A7XIFp8KnDPGEyu3oZ9xuaDuVOA+z51ltybH2zirIeFfj9ecaq73Ww6j+kHmsoy0D1E1XKKdtSoVq4u5Y7FGyBPl4OcCp1G2NLLfnMVfVUuYyauxLN' +
    'SHqyQ0c92Q+qh0H1MKgeBtXDoHoYVA+D6mFQPQyqh0H1MPZTrz55rPiw8Y+gehhUD+M31asvsfoD7HtVj/WH32ohYZ5j/2OvLpmpDsABUd+juIk2LP' +
    'bjjp0W5vlBwKhPE0rLbErGSdwyLqJMgTrBbHATaahn8PIh6vNYKpIZO9aobm/mAq9R3NcFLfwZv8yx1YfuAvWmw0f1yyYUB1avGrQLvHpxHRQciQOr' +
    'd+JEbN5gTPcL1AT+aZzkrbdkk7ak/LPpUqCfhqYsIznRb/00uVTIOyubpjnfYUImE7IXy37qEdiHe2jOrN6+zsIRfk+6nPytb9g/wws/87d+cE4F1c' +
    'OgehhUD4PqYVA9DKqHQfUwqB7E6/UDn8M1JemPJB8AAAAASUVORK5CYII=';
  private readonly kyloeLogo;

  constructor(private loggingService: LoggingService) {
    this.kyloeLogo = new Image();
    this.kyloeLogo.src = 'assets/icon-128.png';
  }

  async update(wordContext: Word.RequestContext, fieldContext: FieldContext, contentControl: ContentControlId): Promise<void> {
    await wordContext.sync();
    await this.updateContentControl(
      wordContext,
      contentControl.id,
      this.getTagTitle(fieldContext),
      this.getTagColour(fieldContext),
      this.getControlTag(contentControl.tag, fieldContext)
    );
  }

  async insert(wordContext: Word.RequestContext, fieldContext: FieldContext, refTag: string): Promise<number> {
    await wordContext.sync();

    const contentControlRange = wordContext.document.getSelection();

    const contentControl = await this.buildContentControl(wordContext, fieldContext, refTag, contentControlRange);

    if (fieldContext.isIterable || fieldContext.isContainer || fieldContext.isConditionalField) {
      contentControl.select('Start');
    } else {
      contentControl.getRange('After').select('Start');
    }

    await wordContext.sync();

    return contentControl.id;
  }

  async insertFromPaste(context: Word.RequestContext, ooxml: string): Promise<void> {
    const range = context.document.getSelection();
    context.load(range);
    range.insertOoxml(ooxml, Word.InsertLocation.replace);
    range.select('End');
    await context.sync();
  }

  replacePlaceholderPicture(fieldContext: FieldContext, contentControl: Word.ContentControl): void {
    if (fieldContext.isImageField) {
      this.addPlaceholderPicture(contentControl);
    }
  }

  private async buildContentControl(
    wordContext: Word.RequestContext,
    fieldContext: FieldContext,
    refTag: string,
    range: Word.Range
  ): Promise<Word.ContentControl> {
    const contentControlTitle = this.getTagTitle(fieldContext);
    const contentControlColour = this.getTagColour(fieldContext);

    const tempParagraph = await this.tableSdtBlockFix(fieldContext, range, wordContext);

    const contentControl = this.createContentControl(range, contentControlTitle, refTag, contentControlColour);
    if (fieldContext.isIterable || fieldContext.isContainer || fieldContext.isConditionalField) {
      // dont do anything
    } else if (fieldContext.isImageField) {
      this.addPlaceholderPicture(contentControl);
    } else if (fieldContext?.stringFormat?.renderHtml) {
      const contentControlText = this.getControlText(fieldContext);
      contentControl.insertText(`|${contentControlText}\nDo not remove this text|`, 'Start');
    } else {
      const contentControlText = this.getControlText(fieldContext);
      contentControl.insertText(`|${contentControlText}|`, 'Start');
    }

    if (tempParagraph) {
      tempParagraph.delete();
    }

    await wordContext.sync();
    return contentControl;
  }

  private async tableSdtBlockFix(fieldContext: FieldContext, range: Word.Range, wordContext: Word.RequestContext) {
    const isHtmlContentControl = fieldContext?.stringFormat?.renderHtml;
    if (!isHtmlContentControl) {
      return undefined;
    }

    range.parentTableCellOrNullObject.load('isNullObject');
    await wordContext.sync();
    const outsideTableCell =  range.parentTableCellOrNullObject.isNullObject;
    if (outsideTableCell) {
      return undefined;
    }

    // over-riding the only paragraph in a table cell will result in a CTSdtCell
    // this causes errors on the server when the html content is inserted server side.
    return range.insertParagraph(' ', 'Before');
  }

  private createContentControl(range: Word.Range, title: string, tag: string, colour: string): Word.ContentControl {
    const contentControl = range.insertContentControl();
    contentControl.title = title;
    contentControl.tag = tag;
    contentControl.appearance = 'BoundingBox';
    // contentControl.appearance = 'Tags';
    contentControl.color = colour;
    // without this, the placeholder text 'Click or tap here to enter text.' is stored multiple times in the glossary
    // however, it does not prevent that text being in the final document as Word replaces empty content control text
    // with the above default placeholder text
    contentControl.placeholderText = '';
    return contentControl;
  }

  private async updateContentControl(wordContext: Word.RequestContext, id: number, title?: string, colour?: string, tag?: string) {
    if (!id || (!title && !colour)) {
      return;
    }

    const contentControl: Word.ContentControl = wordContext.document.contentControls.getById(id);
    if (title) {
      contentControl.title = title;
    }
    if (colour) {
      contentControl.color = colour;
    }

    if (tag) {
      contentControl.tag = tag;
    }

    await wordContext.sync();
  }

  private addPlaceholderPicture(contentControl: Word.ContentControl): void {
    const canvas = document.createElement('canvas') as HTMLCanvasElement;
    const ctx = canvas.getContext('2d');

    if (ctx) {
      ctx.fillStyle = '#F4F4F4';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      if (this.kyloeLogo) {
        ctx.globalAlpha = 0.05;
        ctx.drawImage(this.kyloeLogo, canvas.width - this.kyloeLogo.width * 0.9, canvas.height - this.kyloeLogo.height * 0.75);
        ctx.globalAlpha = 1.0;
      }

      const placeholderText = 'Placeholder image';
      ctx.fillStyle = '#333F48';
      ctx.font = '22px Arial';
      const textMetrics = ctx.measureText(placeholderText);
      const textWidth = textMetrics.width;
      const textHeight = textMetrics.actualBoundingBoxAscent + textMetrics.actualBoundingBoxDescent;
      const cx = (canvas.width - textWidth) / 2;
      const cy = (canvas.height + textHeight) / 2.05;
      ctx.fillText(placeholderText, cx, cy);

      // bottom right timestamp to make image unique
      ctx.fillStyle = '#BBBBBB';
      ctx.font = '8px Arial';
      const timestamp = `${Date.now()}`;
      const timestampMetrics = ctx.measureText(timestamp);
      const timestampWidth = timestampMetrics.width;
      const timestampHeight = timestampMetrics.actualBoundingBoxAscent + timestampMetrics.actualBoundingBoxDescent;
      const tx = canvas.width - timestampWidth - 6;
      const ty = canvas.height - timestampHeight;
      ctx.fillText(timestamp, tx, ty);

      const dataUrl = canvas.toDataURL('image/png'); // data:image/png;base64,iVBOR....
      const b64 = dataUrl.substring('data:image/png;base64,'.length);
      contentControl.insertInlinePictureFromBase64(b64, 'Replace');

      contentControl.styleBuiltIn = Word.BuiltInStyleName.noSpacing;
    } else {
      this.loggingService.error(
        'ContentControlService',
        'addPlaceholderPicture',
        'Canvas context is null. Defaulting to single placeholder. This will only work for one image!'
      );
      contentControl.insertInlinePictureFromBase64(ContentControlService.PLACEHOLDER_IMAGE, 'Replace');
    }
  }

  private getTagTitle(fieldContext: FieldContext): string {
    return fieldContext.field.path;
  }

  private getTagColour(fieldContext: FieldContext): string {
    if (fieldContext.conditions) {
      return '#0000FF';
    }

    if (fieldContext.isIterable) {
      return '#00FF00';
    }

    if (fieldContext.isContainer) {
      return '#AA00AA';
    }

    if (fieldContext.isImageField) {
      return '#333F48';
    }

    return '#FFBE50';
  }

  private getControlText(fieldContext: FieldContext): string {
    let parent = fieldContext.field.parent;
    while (parent !== undefined && parent.type !== FieldType.TO_ONE && parent.type !== FieldType.TO_MANY) {
      parent = parent.parent;
    }
    if (parent !== undefined) {
      return `${parent.label}-${fieldContext.field.label}`;
    }
    return fieldContext.field.label;
  }

  private getControlTag(refTag: string, fieldContext: FieldContext): string {
    const tag = Tag.fromString(refTag);

    // the dope part creates the original tag, we could move this update into the controller service (tag builder class?)
    // forces the tag to get the update from the field context
    tag.contentType = fieldContext.stringFormat?.renderHtml ? `application/xhtml+xml` : '';

    return tag.toString();
  }
}
